import type { DateRange as DayPickerRange } from "react-day-picker";

export type ReportDatePreset = "1W" | "1M" | "MTD" | "YTD" | "All";

const PRESETS: ReportDatePreset[] = ["1W", "1M", "MTD", "YTD", "All"];

export function formatYmdLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseYmdLocal(ymd: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  const dt = new Date(y, mo, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo || dt.getDate() !== d) return null;
  return dt;
}

export function parseReportDateSearchParams(
  searchParams: URLSearchParams,
  { defaultPreset }: { defaultPreset: ReportDatePreset },
): { preset: ReportDatePreset; customRange: DayPickerRange | undefined } {
  const startStr = searchParams.get("start");
  const endStr = searchParams.get("end");
  if (startStr && endStr) {
    const from = parseYmdLocal(startStr);
    const to = parseYmdLocal(endStr);
    if (from && to && from.getTime() <= to.getTime()) {
      return { preset: "All", customRange: { from, to } };
    }
  }
  const dr = searchParams.get("dateRange");
  if (dr && (PRESETS as readonly string[]).includes(dr)) {
    return { preset: dr as ReportDatePreset, customRange: undefined };
  }
  return { preset: defaultPreset, customRange: undefined };
}

export function applyReportDateToSearchParams(
  params: URLSearchParams,
  {
    preset,
    customRange,
  }: { preset: ReportDatePreset; customRange: DayPickerRange | undefined },
) {
  params.delete("start");
  params.delete("end");
  params.delete("dateRange");
  if (customRange?.from && customRange?.to) {
    params.set("start", formatYmdLocal(customRange.from));
    params.set("end", formatYmdLocal(customRange.to));
    return;
  }
  // Always persist preset (including "All") so URLs round-trip; empty params would fall back to caller defaultPreset.
  params.set("dateRange", preset);
}

export function reportDateFilterToQueryString({
  preset,
  customRange,
}: {
  preset: ReportDatePreset;
  customRange: DayPickerRange | undefined;
}): string {
  const p = new URLSearchParams();
  applyReportDateToSearchParams(p, { preset, customRange });
  return p.toString();
}
